import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { globalTheme, IfScope, useFeatureFlags } from '@playbookux/playbook-web-core';
import { useQuery } from '@apollo/client';
import Demographics from '../../../../../core/components/demographics';
import EmploymentInfo from '../employment-info';
import Screener from '../../../../components/screener';

import Avatar from '../../../../../core/components/avatar';
import CardSortModal from '../../../../components/card-sort-modal';
import TreeTestModal from '../../../../components/tree-test-modal';

import typeTest from '../../../../../core/constants/testTypes';
import { calculateAge } from '../../../../../core/utils/calculate-age';

import ParticipantConsentForm from '../consent-form';
import { GET_PROJECT_CONSENT_FORMS } from '../../../../../core/graphql/consent-forms';
import MenuFlaps from '../menu-flaps';
import {
  DELETE_TEXT, DELETE_VIDEO_TEXT, DID_NOT_SHOW,
  TABS_OPTIONS,
} from './constants';
import {
  Arrow,
  Card,
  CardHeader,
  CardRow,
  ColumnText,
  Container,
  ContainerButtons,
  ContainerColumn,
  ContainerDelete,
  ContainerNewText,
  ContainerVideo,
  CustomClampLines,
  DeleteText,
  Icon,
  Image,
  ImageContainer,
  NewVideoText,
  Spacer,
  UserAvatar,
  VideoIcon,
} from './styles';
import EditModalUpload from './components/edit-modal-upload';

const ParticipantAccordion = (props) => {
  const {
    index,
    onClickDelete,
    onOpenTestSession,
    project,
    tester,
    refetch,
    scope,
  } = props;

  const {
    testType = '',
    participantsSource,
    company,
  } = project;

  const isPlaybookParticipant = participantsSource === 'playbook';

  const {
    user,
    screenerSession,
    testSessions,
    segment,
    qualifiedAt,
    userInterviewsParticipantId,
  } = tester;
  const { surveys } = segment;
  const {
    newVideo,
    id: testSessionId,
    bookingStatus,
  } = testSessions[0];
  const { profile } = user;

  const [showCard, setShowCard] = useState(false);
  const [activeItem, setActiveItem] = useState(TABS_OPTIONS.DEMOGRAPHICS);
  const [showModal, setShowModal] = useState(false);
  const {
    featureFlags: { doNotShowLogo },
  } = useFeatureFlags(company.id);

  const {
    data: consentFormsData,
  } = useQuery(GET_PROJECT_CONSENT_FORMS, {
    variables: {
      projectId: project.id,
    },
  });

  const { projectConsentForms = [] } = consentFormsData?.project || {};

  const imgUrlSource = {
    [typeTest.CARD_SORT]: '/images/card-sort.png',
    [typeTest.TREE_TEST]: '/images/tree-test.png',
    [typeTest.SURVEY]: '/images/attached.png',
  };

  useEffect(() => {
    let newActiveItem = TABS_OPTIONS.DEMOGRAPHICS;

    if (testType === typeTest.MODERATED) {
      newActiveItem = TABS_OPTIONS.CONSENT_FORM;
    } else if (isPlaybookParticipant) {
      newActiveItem = TABS_OPTIONS.DEMOGRAPHICS;
    } else if (tester.user.guest || !isPlaybookParticipant) {
      newActiveItem = TABS_OPTIONS.SCREENER;
    }

    setShowCard(false);
    setActiveItem(newActiveItem);
  }, [isPlaybookParticipant, tester.user.guest, testType]);

  const onClickMenuItem = (e, { name }) => {
    setActiveItem(name);
  };

  const onClickShowCard = () => {
    setShowCard((previousState) => !previousState);
  };

  const handleOpenModal = () => {
    setShowModal(true);
    onOpenTestSession();
  };

  const goToResponses = (evt) => {
    evt.stopPropagation();
    onOpenTestSession();
    window.open(`/projects/${project.id}/participants-response/${testSessionId}`, '_blank');
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const goToVideo = (evt) => {
    evt.stopPropagation();
    onOpenTestSession();
    window.open(`/projects/${project.id}/videos/${tester.id}`, '_blank');
  };

  const avatarColors = globalTheme.color.avatarBackground;
  const avatarColor = avatarColors[index % avatarColors.length];
  const isCardSort = testType === typeTest.CARD_SORT;
  const isTreeTest = testType === typeTest.TREE_TEST;
  const isModerated = testType === typeTest.MODERATED;
  const isUnmoderated = testType === typeTest.UNMODERATED;
  const isSurvey = testType === typeTest.SURVEY;
  const isUpload = testType === typeTest.UPLOAD;
  const participantAge = profile?.birthdate ? calculateAge(profile.birthdate).toString() : '';
  const isUserInterviewsRecruited = !!(!doNotShowLogo && userInterviewsParticipantId && !isModerated);

  return (
    <Container>
      {isCardSort && showModal && (
        <CardSortModal
          show
          onClose={handleCloseModal}
          hideCardName={project.hideName}
          testSessionId={testSessionId}
        />
      )}

      {isTreeTest && showModal && (
        <TreeTestModal
          show
          onClose={handleCloseModal}
          testSessionId={testSessionId}
        />
      )}

      <ContainerNewText>
        {newVideo
          ? <NewVideoText>NEW</NewVideoText>
          : <Spacer />}
      </ContainerNewText>
      <CardRow>
        <Card>
          <CardHeader onClick={onClickShowCard}>
            <ContainerColumn flex={1}>
              <UserAvatar>
                <Avatar
                  color={avatarColor}
                  firstName={profile.firstName}
                />
              </UserAvatar>
            </ContainerColumn>

            <ContainerColumn flex={1}>
              <ColumnText>
                <CustomClampLines
                  text={profile.firstName}
                  lines={1}
                  ellipsis="..."
                  buttons={false}
                />
              </ColumnText>
            </ContainerColumn>

            <ContainerColumn flex={1}>
              <ColumnText>
                <CustomClampLines
                  text={participantAge}
                  lines={1}
                  ellipsis="..."
                  buttons={false}
                />
              </ColumnText>
            </ContainerColumn>

            <ContainerColumn flex={3}>
              <ColumnText flexSize={3}>
                <CustomClampLines
                  text={profile.raceEthnicHeritage}
                  lines={1}
                  ellipsis="..."
                  buttons={false}
                />
              </ColumnText>
            </ContainerColumn>

            <ContainerColumn flex={1}>
              <ColumnText>
                <CustomClampLines
                  text={profile.gender}
                  lines={1}
                  ellipsis="..."
                  buttons={false}
                />
              </ColumnText>
            </ContainerColumn>

            <ContainerColumn isLast>
              <Arrow>
                <Icon
                  size="small"
                  name={showCard ? 'chevron up' : 'chevron down'}
                />
              </Arrow>
            </ContainerColumn>
          </CardHeader>

          {showCard && (
            <>
              <MenuFlaps
                activeItem={activeItem}
                onClickMenuItem={onClickMenuItem}
                project={project}
              />

              {isPlaybookParticipant && (activeItem === TABS_OPTIONS.DEMOGRAPHICS) && (
                <Demographics
                  profile={user?.profile}
                  showTitle={false}
                  showEmail={false}
                  showUserInterviewsRecruited={isUserInterviewsRecruited}
                />
              )}

              {isPlaybookParticipant && (activeItem === TABS_OPTIONS.EMPLOYMENT) && (
                <EmploymentInfo
                  user={user}
                  compact={false}
                  showUserInterviewsRecruited={isUserInterviewsRecruited}
                />
              )}

              {(activeItem === TABS_OPTIONS.SCREENER) && (
                <Screener
                  screenerSession={screenerSession}
                  surveys={surveys}
                  segment={segment}
                  showUserInterviewsRecruited={isUserInterviewsRecruited}
                />
              )}

              {
                (activeItem === TABS_OPTIONS['CONSENT-FORM']) && (
                  <ParticipantConsentForm
                    consentForms={projectConsentForms}
                    qualifiedAt={qualifiedAt}
                    showUserInterviewsRecruited={isUserInterviewsRecruited}
                  />
                )
              }
              <IfScope user scope={scope}>
                <ContainerButtons>
                  {isUpload && (
                    <EditModalUpload testerId={tester.id} profileFirstName={profile.firstName} refetch={refetch} />
                  )}
                  {!isPlaybookParticipant && !isCardSort && !isTreeTest && (
                    <ContainerDelete>
                      <DeleteText onClick={() => onClickDelete(tester.id)}>
                        {isSurvey ? (<>{DELETE_TEXT}</>) : (<>{DELETE_VIDEO_TEXT}</>)}

                      </DeleteText>
                    </ContainerDelete>
                  )}
                </ContainerButtons>
              </IfScope>
            </>
          )}
        </Card>

      </CardRow>

      <ContainerVideo>
        {(isCardSort || isTreeTest) ? (
          <ImageContainer onClick={handleOpenModal} isTreeTest={isTreeTest}>
            <Image src={imgUrlSource[testType] || ''} />
          </ImageContainer>
        ) : null}

        {(isSurvey) ? (
          <ImageContainer onClick={goToResponses} isSurvey={isSurvey}>
            <Image src={imgUrlSource[testType] || ''} />
          </ImageContainer>
        ) : null}

        {((isModerated || isUnmoderated || isUpload) && bookingStatus !== DID_NOT_SHOW) ? (
          <VideoIcon
            onClick={goToVideo}
            size="huge"
            name="play circle"
          />
        ) : null}
      </ContainerVideo>
    </Container>
  );
};

ParticipantAccordion.propTypes = {
  index: PropTypes.number.isRequired,
  project: PropTypes.shape({
    id: PropTypes.string,
    testType: PropTypes.string,
    participantsSource: PropTypes.string,
    surveys: PropTypes.arrayOf(PropTypes.shape()),
    hideName: PropTypes.bool,
    company: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  tester: PropTypes.shape({
    id: PropTypes.string,
    user: PropTypes.shape({
      profile: PropTypes.shape({
        id: PropTypes.string,
        linkedinFields: PropTypes.shape(),
        firstName: PropTypes.string,
        raceEthnicHeritage: PropTypes.string,
        gender: PropTypes.string,
        birthdate: PropTypes.string,
      }),
      guest: PropTypes.bool,
    }),
    screenerSession: PropTypes.shape({
      id: PropTypes.string,
    }),
    testSessions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        newVideo: PropTypes.bool,
        bookingStatus: PropTypes.string,
        completedAt: PropTypes.string,
      }),
    ),
    segment: PropTypes.shape({
      surveys: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    qualifiedAt: PropTypes.string,
    userInterviewsParticipantId: PropTypes.string,
  }).isRequired,
  onOpenTestSession: PropTypes.func.isRequired,
  onClickDelete: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  scope: PropTypes.string.isRequired,
};

export default ParticipantAccordion;
